import { useEffect, type RefObject } from 'react';
import { getSearchFocusableElements } from '../search-focusable-utils';

/**
 * Keeps keyboard focus inside `containerRef` while `active` (Tab / Shift+Tab wrap, focusin guard).
 */
export function useSearchOverlayFocusTrap({
    active,
    containerRef,
    onTabFromLast,
}: {
    active: boolean;
    containerRef: RefObject<HTMLElement | null>;
    /** When set, Tab from the last focusable exits the trap (e.g. close overlay and return to header input). */
    onTabFromLast?: () => void;
}): void {
    useEffect(() => {
        if (!active) return;

        const getFocusables = (): HTMLElement[] => {
            const root = containerRef.current;
            if (!root) return [];
            return getSearchFocusableElements(root);
        };

        const onDocumentKeyDownCapture = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;
            const root = containerRef.current;
            if (!root) return;
            const pathTarget = e.target as Node | null;
            if (!pathTarget || !root.contains(pathTarget)) return;

            const list = getFocusables();
            if (list.length === 0) return;

            const first = list[0];
            const last = list[list.length - 1];
            const activeEl = document.activeElement as HTMLElement | null;
            if (!activeEl || !list.includes(activeEl)) return;

            if (e.shiftKey) {
                if (activeEl === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else if (activeEl === last) {
                e.preventDefault();
                if (onTabFromLast) {
                    onTabFromLast();
                } else {
                    first.focus();
                }
            }
        };

        const onDocumentFocusInCapture = (e: FocusEvent) => {
            const root = containerRef.current;
            if (!root) return;
            const next = e.target as Node | null;
            if (!next || root.contains(next)) return;
            const list = getFocusables();
            list[0]?.focus();
        };

        document.addEventListener('keydown', onDocumentKeyDownCapture, true);
        document.addEventListener('focusin', onDocumentFocusInCapture, true);

        return () => {
            document.removeEventListener('keydown', onDocumentKeyDownCapture, true);
            document.removeEventListener('focusin', onDocumentFocusInCapture, true);
        };
    }, [active, containerRef, onTabFromLast]);
}
